/**
 * Fias Leads — CSV import React Query hooks.
 *
 * Separate from useFiasLeads.ts (already 1,000+ lines). Conventions
 * match: holderId is `string | null`, queries gate on `enabled:
 * !!holderId`, mutations invalidate holder-scoped keys.
 *
 * Preview and execute are mutations, not queries: their payloads are
 * whole row envelopes (not cacheable server state), and the wizard
 * drives them imperatively via mutateAsync in its chunk loop. Entity
 * list invalidation after an import lands is the WIZARD's job (once,
 * after the final chunk) — useExecuteFiasLeadsImportChunk deliberately
 * skips per-chunk invalidation so a 5,000-row import doesn't refetch
 * the contacts list 25 times.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  FiasLeadsImportDedupStrategy,
  FiasLeadsImportEntityType,
  FiasLeadsImportNameFallback,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useFiasLeadsImportBatches(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.fiasLeads.importBatches(holderId ?? ''),
    queryFn: () => api.fiasLeadsImportBatches(holderId!),
    select: (data) => data.batches,
    enabled: !!holderId,
  });
}

export function useCreateFiasLeadsImportBatch(holderId: string | null) {
  return useMutation({
    mutationFn: (input: {
      entityType: FiasLeadsImportEntityType;
      fileName: string;
      dedupStrategy?: FiasLeadsImportDedupStrategy;
      nameFallback?: FiasLeadsImportNameFallback;
      pipelineId?: string;
      defaultStageId?: string;
      totalRows?: number;
    }) => api.fiasLeadsImportCreateBatch(holderId!, input),
  });
}

export function useFiasLeadsImportPreview(holderId: string | null) {
  return useMutation({
    mutationFn: (args: {
      entityType: FiasLeadsImportEntityType;
      rows: Array<Record<string, string>>;
      dedupStrategy?: FiasLeadsImportDedupStrategy;
      nameFallback?: FiasLeadsImportNameFallback;
      pipelineId?: string;
      defaultStageId?: string;
      startIndex?: number;
      rowIndexes?: number[];
      knownKeys?: string[];
    }) => {
      const { entityType, ...body } = args;
      return api.fiasLeadsImportPreview(holderId!, entityType, body);
    },
  });
}

export function useExecuteFiasLeadsImportChunk(holderId: string | null) {
  return useMutation({
    mutationFn: (args: {
      entityType: FiasLeadsImportEntityType;
      batchId: string;
      rows: Array<Record<string, string>>;
      startIndex?: number;
      rowIndexes?: number[];
    }) => {
      const { entityType, ...body } = args;
      return api.fiasLeadsImportExecute(holderId!, entityType, body);
    },
  });
}

export function useFinalizeFiasLeadsImportBatch(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) => api.fiasLeadsImportBatchFinalize(holderId!, batchId),
    onSuccess: () => {
      if (holderId) {
        qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.importBatches(holderId) });
      }
    },
  });
}

/** Invalidates the entity lists a batch archive/restore mutates. */
function invalidateEntityLists(
  qc: ReturnType<typeof useQueryClient>,
  holderId: string,
  entityType: FiasLeadsImportEntityType,
): void {
  qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.importBatches(holderId) });
  if (entityType === 'contact') {
    qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.contacts(holderId) });
  } else if (entityType === 'company') {
    qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.companies(holderId) });
    // A company import retroactively links contacts whose imported
    // company_name matches (server link-on-create pass) — contact
    // lists must refetch to show the new links.
    qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.contacts(holderId) });
  } else {
    qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.deals(holderId) });
    qc.invalidateQueries({ queryKey: queryKeys.fiasLeads.dashboard(holderId) });
  }
}

export function useArchiveFiasLeadsImportBatch(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { batchId: string; entityType: FiasLeadsImportEntityType }) =>
      api.fiasLeadsImportBatchArchive(holderId!, args.batchId),
    onSuccess: (_data, args) => {
      if (holderId) invalidateEntityLists(qc, holderId, args.entityType);
    },
  });
}

export function useRestoreFiasLeadsImportBatch(holderId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { batchId: string; entityType: FiasLeadsImportEntityType }) =>
      api.fiasLeadsImportBatchRestore(holderId!, args.batchId),
    onSuccess: (_data, args) => {
      if (holderId) invalidateEntityLists(qc, holderId, args.entityType);
    },
  });
}
